import { useAppDispatch, useAppSelector } from "@/lib/store";
import { useEffect, useCallback, useRef } from "react";
import {
  fetchIndividualDailyWorkDays,
  setStartDate,
  setEndDate,
  setSearch,
  setStatus,
  setPage,
  setPageSize,
  setSortKey,
} from "./daily-work.slice";

export const useIndividualDailyWork = () => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((s) => s.individualDailyWork);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    const fetchData = async () => {
      try {
        await dispatch(fetchIndividualDailyWorkDays()).unwrap();
      } catch (error: any) {
        if (error.message !== "Request cancelled") {
          console.error("Failed to fetch daily work days:", error);
        }
      }
    };

    fetchData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [
    dispatch,
    state.filters.startDate,
    state.filters.endDate,
    state.filters.search,
    state.filters.status,
    state.filters.sortKey,
    state.filters.sortOrder,
    state.pagination.page,
    state.pagination.pageSize,
  ]);

  const setStartDateCallback = useCallback((v: string) => dispatch(setStartDate(v)), [dispatch]);
  const setEndDateCallback = useCallback((v: string) => dispatch(setEndDate(v)), [dispatch]);
  const setSearchCallback = useCallback((v: string) => dispatch(setSearch(v)), [dispatch]);
  const setStatusCallback = useCallback((v: string) => dispatch(setStatus(v)), [dispatch]);
  const setSortKeyCallback = useCallback((k: string) => dispatch(setSortKey(k)), [dispatch]);
  const setPageCallback = useCallback((p: number) => dispatch(setPage(p)), [dispatch]);
  const setPageSizeCallback = useCallback((ps: number) => dispatch(setPageSize(ps)), [dispatch]);

  return {
    list: state.list,
    total: state.total,
    loading: state.loading,
    filters: {
      ...state.filters,
      setStartDate: setStartDateCallback,
      setEndDate: setEndDateCallback,
      setSearch: setSearchCallback,
      setStatus: setStatusCallback,
      setSortKey: setSortKeyCallback,
    },
    pagination: {
      ...state.pagination,
      setPage: setPageCallback,
      setPageSize: setPageSizeCallback,
    },
    dispatch,
  };
};
